import { useEffect, useState } from "react"
import { saveInfluencerFavourite, removeInfluencerFavourite } from "../influencerFavouritesApi"
import { triggerToast } from "../../../utils/toast"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"

/**
 * Optimistic save/unsave for one influencer - the interaction pattern is new
 * to this codebase (there's no existing favourite/bookmark feature to
 * follow), designed to match ordinary bookmark-icon UX: the icon flips the
 * instant it's clicked, and only a *failure* interrupts that (revert + a
 * toast explaining why) - success is silent, same as any other bookmark
 * toggle. `{platform, username}` is enough to identify the influencer on
 * both writes (INFLUENCER_FAVOURITES_API.md §2) even for YouTube's
 * channel-id identity, since `username` is resolved through the store
 * server-side.
 *
 * `initialIsFavourite` re-syncs whenever the caller's own row changes (a
 * fresh page of results, or switching to a different creator's profile) -
 * see the effect below.
 */
export default function useInfluencerFavourite({ platform, username, initialIsFavourite = false }) {
  const [isFavourite, setIsFavourite] = useState(initialIsFavourite)
  const [isSaving, setIsSaving] = useState(false)
  const { trackEvent } = useAnalytics()

  useEffect(() => {
    setIsFavourite(initialIsFavourite)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, username, initialIsFavourite])

  const toggle = async (e) => {
    e?.stopPropagation?.()
    e?.preventDefault?.()
    if (isSaving || !platform || !username) return

    const next = !isFavourite
    setIsFavourite(next) // optimistic
    setIsSaving(true)

    try {
      if (next) {
        await saveInfluencerFavourite({ platform, username })
      } else {
        await removeInfluencerFavourite({ platform, username })
      }
      trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_FAVOURITE_TOGGLED, { platform, username, is_favourite: next })
    } catch (err) {
      setIsFavourite(!next) // revert
      // §2 - 409 means the list is full; the response body names the cap,
      // which is worth surfacing verbatim rather than a generic failure.
      const message = err?.status === 409
        ? (err.data?.error || "Your favourites list is full.")
        : (err.message || "Couldn't update your favourites - try again.")
      triggerToast(message, "error")
    } finally {
      setIsSaving(false)
    }
  }

  return { isFavourite, isSaving, toggle }
}
